/**
 * Broker Vuex Store Module
 * Manages broker-specific state and authentication
 */

import { Module } from 'vuex'
import authRepository from '../repositories/auth.repository'
import partnerService, { BrokerInfo } from '../services/partner.service'

export interface BrokerUser {
  id: number // Real numeric user ID from /api/account
  username: string // Login username
  email: string
  firstName: string
  lastName: string
  imageUrl?: string
  roles: string[]
  partnerId: number
  mainClubId?: number | null
  partnerStaffRole?: string 
}

export interface BrokerState {
  // Authentication
  isAuthenticated: boolean
  token: string | null
  partnerId: number | null
  user: BrokerUser | null

  // Broker/Partner Info
  brokerInfo: BrokerInfo | null

  // UI State
  loading: boolean
  error: string | null
}

const brokerModule: Module<BrokerState, any> = {
  namespaced: true,

  state: {
    isAuthenticated: false,
    token: null,
    partnerId: null,
    user: null,
    brokerInfo: null,
    loading: false,
    error: null
  },

  mutations: {
    SET_AUTH(state, { token, partnerId, user }) {
      state.isAuthenticated = true
      state.token = token
      state.partnerId = partnerId
      state.user = user
    },

    CLEAR_AUTH(state) {
      state.isAuthenticated = false
      state.token = null
      state.partnerId = null
      state.user = null
      state.brokerInfo = null
    },

    SET_BROKER_INFO(state, brokerInfo: BrokerInfo | null) {
      state.brokerInfo = brokerInfo
    },

    SET_LOADING(state, loading: boolean) {
      state.loading = loading
    },

    SET_ERROR(state, error: string | null) {
      state.error = error
    },

    SET_USER(state, user: BrokerUser) {
      state.user = user
    },

    SET_MAIN_CLUB_ID(state, clubId: number | null) {
      if (state.user) {
        state.user.mainClubId = clubId
      }
    }
  },

  actions: {
    async login({ commit, dispatch }, { username, password }) {
      commit('SET_LOADING', true)
      commit('SET_ERROR', null)

      try {
        const response = await authRepository.login(username, password)

        console.log('Login response:', response) // Debug log

        // Create initial user object from response
        // Note: id will be set to the real user ID when fetchUserAccount is called
        const user: BrokerUser = {
          id: 0, // Temporary - will be replaced with real ID from /api/account
          username: response.user.username,
          email: username,
          firstName: '',
          lastName: '',
          roles: response.user.roles,
          partnerId: response.partnerId
        }

        commit('SET_AUTH', {
          token: response.token,
          partnerId: response.partnerId,
          user
        })

        // Fetch full user account details including profile picture
        try {
          await dispatch('fetchUserAccount')
        } catch (error) {
          console.error('Failed to fetch user account details after login:', error)
          // Don't fail login if user account fetch fails
        }

        console.log('Auth set successfully, returning success') // Debug log
        return { success: true }
      } catch (error: any) {
        console.error('Login error in store:', error) // Debug log
        const errorMessage = error.response?.data?.message || error.message || 'Invalid credentials'
        commit('SET_ERROR', errorMessage)
        return { success: false, error: errorMessage }
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async logout({ commit }) {
      await authRepository.logout()
      commit('CLEAR_AUTH')
    },

    async checkAuth({ commit, dispatch }) {
      if (authRepository.isAuthenticated()) {
        const token = authRepository.getToken()
        const partnerId = authRepository.getPartnerId()
        const username = localStorage.getItem('broker_user')

        if (token && partnerId && username) {
          // Reconstruct initial user from stored data
          // Note: id will be set to the real user ID when fetchUserAccount is called
          const user: BrokerUser = {
            id: 0, // Temporary - will be replaced with real ID from /api/account
            username,
            email: '',
            firstName: '',
            lastName: '',
            roles: [],
            partnerId
          }

          commit('SET_AUTH', { token, partnerId, user })

          // Fetch full user account details including profile picture
          try {
            await dispatch('fetchUserAccount')
          } catch (error) {
            console.error('Failed to fetch user account details on auth check:', error)
            // Don't fail auth check if user account fetch fails
          }

          return true
        }
      }

      commit('CLEAR_AUTH')
      return false
    },

    async setMainClubId({ commit, state }, clubId: number | null) {
      if (state.user) {
        commit('SET_MAIN_CLUB_ID', clubId)
      }
    },

    async fetchUserAccount({ commit, state }) {
      try {
        const userAccount = await authRepository.getUser()
        console.log('Fetched user account from /api/account:', userAccount)

        // Update user with account details including the REAL numeric user ID
        if (state.user) {
          const updatedUser: BrokerUser = {
            ...state.user,
            id: userAccount.id, // This is the REAL numeric user ID
            username: userAccount.login, // Keep the login/username
            email: userAccount.email,
            firstName: userAccount.firstName,
            lastName: userAccount.lastName,
            imageUrl: userAccount.imageUrl,
            roles: userAccount.authorities,
            partnerStaffRole: userAccount.partnerStaffRole
          }

          commit('SET_USER', updatedUser)
          console.log('Updated broker user with real ID:', updatedUser)
        }

        return userAccount
      } catch (error) {
        console.error('Failed to fetch user account:', error)
        throw error
      }
    },

    async fetchBrokerInfo({ commit }) {
      try {
        const brokerInfo = await partnerService.getBrokerInfo()
        console.log('Fetched broker info:', brokerInfo)
        commit('SET_BROKER_INFO', brokerInfo)
        return brokerInfo
      } catch (error) {
        console.error('Failed to fetch broker info:', error)
        commit('SET_BROKER_INFO', null)
        return null
      }
    }
  },

  getters: {
    isAuthenticated: (state) => state.isAuthenticated,
    currentUser: (state) => state.user,
    mainClubId: (state) => state.user ? state.user.mainClubId : null,
    partnerId: (state) => state.partnerId,
    authToken: (state) => state.token,
    isLoading: (state) => state.loading,
    error: (state) => state.error,
    brokerInfo: (state) => state.brokerInfo,
    brokerLogoUrl: (state) => {
      if (!state.brokerInfo?.partner) return null
      const { partner } = state.brokerInfo
      // Priority: partnerBrand.responsiveLogo > partner.responsiveLogoUrl > partner.fullLogoUrl
      return partner.partnerBrand?.responsiveLogo || partner.responsiveLogoUrl || partner.fullLogoUrl || null
    },
    brokerName: (state) => {
      if (!state.brokerInfo) return ''
      return state.brokerInfo.partner?.shortName || state.brokerInfo.partner?.fullName || state.brokerInfo.name || ''
    }, 
    isReadOnlyStaff: (state) =>
      state.user?.partnerStaffRole === 'READ_ONLY',
  }
}

export default brokerModule